/**
 * Client helpers for Image Editor → requestImageEditorAi.
 */

/** Closest SDXL aspect labels used by the callable. */
const ASPECT_CANDIDATES = [
  { key: '1:1', ratio: 1 },
  { key: '16:9', ratio: 16 / 9 },
  { key: '9:16', ratio: 9 / 16 },
  { key: '3:2', ratio: 3 / 2 },
  { key: '21:9', ratio: 21 / 9 },
];

/**
 * @param {number} width
 * @param {number} height
 * @returns {string}
 */
export function aspectRatioLabelFromSize(width, height) {
  const w = Number(width) || 0;
  const h = Number(height) || 0;
  if (w <= 0 || h <= 0) return '1:1';
  const ratio = w / h;
  let best = ASPECT_CANDIDATES[0];
  let bestDiff = Infinity;
  for (let i = 0; i < ASPECT_CANDIDATES.length; i += 1) {
    const candidate = ASPECT_CANDIDATES[i];
    const diff = Math.abs(candidate.ratio - ratio);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = candidate;
    }
  }
  return best.key;
}

/**
 * Map callable / Firebase errors to Image Editor toolError copy.
 * Budget gate must not look like a generic failure.
 * @param {unknown} err
 * @returns {string}
 */
export function mapImageEditorAiError(err) {
  const code =
    err && typeof err === 'object' && typeof err.code === 'string'
      ? err.code
      : '';
  const message =
    err && typeof err === 'object' && typeof err.message === 'string'
      ? err.message
      : '';
  const combined = `${code} ${message}`.toLowerCase();

  if (
    code === 'functions/resource-exhausted' ||
    code === 'resource-exhausted' ||
    combined.includes('spend limit') ||
    combined.includes('monthly ai budget') ||
    combined.includes('ai spend')
  ) {
    return (
      'AI spend limit reached. Raise the workspace monthly AI budget ' +
      '(or the project AI spend cap), or wait until next month.'
    );
  }

  if (
    code === 'functions/failed-precondition' ||
    code === 'failed-precondition'
  ) {
    if (combined.includes('stability')) {
      return (
        'Stability API key is not configured. Add it under Workspace ' +
        'Settings → API & integrations.'
      );
    }
  }

  if (
    code === 'functions/unauthenticated' ||
    code === 'unauthenticated'
  ) {
    return 'You must be signed in to generate images.';
  }

  if (
    code === 'functions/permission-denied' ||
    code === 'permission-denied'
  ) {
    return message || 'You do not have permission to generate images.';
  }

  if (message.trim()) return message.trim();
  return 'Image generation failed. Try again in a moment.';
}

/**
 * Decode a data URL / https URL into ImageData for float landing.
 * @param {string} src
 * @returns {Promise<ImageData>}
 */
export function loadImageSrcToImageData(src) {
  const url = typeof src === 'string' ? src.trim() : '';
  if (!url) {
    return Promise.reject(new Error('Empty image payload.'));
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = 'async';
    if (!url.startsWith('data:')) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        if (!canvas.width || !canvas.height) {
          reject(new Error('Generated image had empty dimensions.'));
          return;
        }
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not read generated image pixels.'));
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(ctx.getImageData(0, 0, canvas.width, canvas.height));
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => {
      reject(new Error('Could not decode generated image.'));
    };
    img.src = url;
  });
}
